<template>
  <div v-if="location">
    <div class="container">
      <div v-if="location.pendingChangeRequest">
        <b-alert show :variant="location.changeRequestType === 'EDIT' ? 'primary' : 'success'" class="mt-3 text-left">
          <h3 class="title">
            A request was made to {{ location.changeRequestType }} this listing on {{
              location.changeRequest.updated_at | dateAtTime }} by:
          </h3>
          <b v-if="location.changeRequest.updated_by">{{ location.changeRequest.updated_by.first_name }} {{
            location.changeRequest.updated_by.last_name }}</b>
          <b v-else>Deleted User</b>
          <br />
          <template v-if="location.changeRequest.organization">
            {{ location.changeRequest.organization.name }}
          </template>
          <br />
          <b v-if="location.changeRequest.updated_by">{{ location.changeRequest.updated_by.telephone | telephoneNumber }}
            |
            <a :href="'mailto:{{location.changeRequest.updated_by.email}}'">{{ location.changeRequest.updated_by.email
            }}</a></b>
          <div class="mt-3" v-if="$store.state.auth.user.role === 'admin' || $store.state.auth.user.role === 'editor'">
            Please review the information in the <strong>PENDING</strong> tab to publish or reject the request. A
            notification will be sent to this member's
            organization upon completion.
          </div>
          <div>
            <b-button size="sm" class="mt-3" @click="cancelChangeRequest(location.changeRequestId)">Cancel Change
              Request</b-button>
          </div>
        </b-alert>
      </div>
      <b-card class="mt-5">
        <h1 class="text-center">
          {{ location.name }}
          <router-link
            v-if="(location.changeRequestType != 'ADD' && userIsInOrg()) || $store.state.auth.user.role === 'admin' || $store.state.auth.user.role === 'editor'"
            :to="{ name: 'locations edit', params: { locationId: location._id } }" class="btn btn-secondary">
            <font-awesome-icon icon="edit" /> Edit
          </router-link>
        </h1>
        <div class="row">
          <div class="col-lg-12">
            <div class="text-center">
              <span v-if="location.created_at">
                Created: {{ location.created_at | dateOnlyLong }}
                | Last Updated: {{ location.updated_at | dateOnlyLong }} |
                Version: {{ location.version }}
              </span>
              <span
                v-else-if="!location.created_at && location.versions && location.versions[0] && location.versions[0].updated_at">
                Created: {{ location.versions[0].updated_at | dateOnlyLong }} | Last Updated:
                {{ location.updated_at | dateOnlyLong }} | Version: {{ location.version }}
              </span>
              <span v-else>
                Created: {{ location.updated_at | dateOnlyLong }} | Last Updated: {{
                  location.updated_at | dateOnlyLong }} |
                Version: {{ location.version }}
              </span>
              <br />
              <br />
              <template v-if="location.organization">
                <span>Claimed by:
                  <router-link :to="{ name: 'org detail', params: { orgId: location.organization._id } }">{{
                    location.organization.name }} </router-link>
                  <b-button class="btn" @click="claimLocation()">Dispute Claim</b-button>
                </span>
                <br />
              </template>
              <template v-else-if="location.changeRequest && location.changeRequest.organization">
                <span>Claimed by:
                  <router-link :to="{ name: 'org detail', params: { orgId: location.changeRequest.organization._id } }">{{
                    location.changeRequest.organization.name
                  }}</router-link></span>
                <br />
              </template>
              <template v-else>
                <span class="small"><b>This location has not been claimed.</b></span> <br />
                <span class="small">You may claim this location so your organization can manage it.</span> <br />
                <b-button class="btn" @click="claimLocation()">Claim</b-button>
              </template>
              <span class="small" v-if="location.organization">If you feel this claim is in error, you may dispute the
                ownership of this location by clicking "Dispute Claim" above.</span>
            </div>
          </div>
        </div>
      </b-card>
      <div class="container text-left mt-5">
        <div class="row">
          <div class="col-lg-12">
            <b-tabs>
              <b-tab title="Details" active ref="detailsTab">
                <div class="row">
                  <div class="col-lg-6">
                    <h2 class="mt-4">Location Detail</h2>
                    <div><b>Name:</b> {{ location.name }}</div>
                    <div><b>Google Place Name:</b> {{ location.googlePlaceName }}</div>
                    <div><b>Google Place Id:</b> {{ location.googlePlaceId }}</div>
                    <div>
                      <b>Slug:</b> <a :href="previewUrl" rel="noopener noreferrer" target="_blank"> {{ location.slug }}</a>
                    </div>
                    <div v-if="location.organization">
                      <b>Organization: </b><router-link
                        :to="{ name: 'org detail', params: { orgId: location.organization._id } }">{{
                          location.organization.name }}</router-link>
                    </div>
                    <div><b>Publish:</b> {{ location.publish }}</div>
                    <div>
                      <b>Experience Types:</b>
                      <li v-for="(type, index) in location.experience_types" :key="index">
                        {{ type.name }}
                      </li>
                    </div>
                    <div>
                      <b>Badges:</b>
                      <li v-for="(badge, index) in location.badge_info" :key="index">
                        {{ badge.name }}
                      </li>
                    </div>
                    <div class="descriptions-section">
                      <h2 class="mt-4">Description</h2>
                      <div><b>Summary Description:</b> {{ location.summary }}</div>
                      <br />
                      <div><b>Location Description:</b> <span v-html="location.description"></span></div>
                      <div><b>Precautions:</b> <span v-html="location.precautions"></span></div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <!-- <div class="map-section">
                  <gmap-map :center="map.center" :zoom="map.zoom" style="width:100%; height: 400px;">
                    <gmap-marker :position="coords" />
                  </gmap-map>
                </div> -->
                    <div class="contact-info-section">
                      <h2 class="mt-4">Contact Information</h2>
                      <div><b>Address:</b> {{ location.address }}</div>
                      <div><b>City:</b> {{ location.city }}</div>
                      <div><b>Zip:</b> {{ location.zip }}</div>
                      <div><b>Counties:</b> {{ location.counties ? location.counties.join(', ') : '' }}</div>
                      <div><b>Wheelchair Accessible:</b> {{ location.wheelchair_accessible }}</div>
                      <div v-if="location.website">
                        <div v-if="location.website.includes('http')">
                          <b>Website: </b><a :href="location.website" alt="Opens in a new window" rel="external noopener noreferrer"
                            target="_blank">{{ location.website }}</a>
                        </div>
                        <div v-else><b>Website: </b>{{ location.website }}</div>
                      </div>
                      <div><b>Directions: </b><span v-html="location.directions"></span></div>
                    </div>
                    <div class="social-section">
                      <h2 class="mt-4">Social Networks</h2>
                      <div v-for="(network, index) in location.social_networks" :key="index">
                        <b>{{ network.network_name }}:</b> {{ network.account_name }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="location-type">
                  <h2 class="mt-4">Category Details</h2>
                  <b-card class="mt-3">
                    <div class="accommodation-info" v-if="location.accommodations">
                      <div v-for="(accommodation, index) in location.accommodations" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> accommodation</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div v-if="accommodation.reservationsUrl && accommodation.reservationsUrl.includes('http')">
                              <b>Reservations Website Address: </b><a :href="accommodation.reservationsUrl"
                                alt="Opens in a new window" rel="external noopener noreferrer" target="_blank">{{
                                  accommodation.reservationsUrl
                                }}</a>
                            </div>
                            <div v-else><b>Reservations Website Address: </b>{{ accommodation.reservationsUrl }}</div>
                            <div v-if="accommodation.phone">
                              <b>Reservations Phone Number: </b>{{ accommodation.phone | telephoneNumber }}
                            </div>
                            <div><b>Number of Rooms/Spaces/Units: </b> {{ accommodation.capacity }}</div>
                            <div><b>Rates:</b> {{ accommodation.rates }}</div>
                            <div><b>Meeting Facilities:</b> {{ accommodation.meetingFacilities }}</div>
                            <div v-if="accommodation.meetingFacilities"><b>Meeting Capacity: </b>{{
                              accommodation.meetingCapacity }}</div>
                            <div><b>Hours of Operation:</b> {{ accommodation.hoursOfOperation }}</div>
                          </div>
                          <div class="col-lg-6">
                            <div><b>Pets Allowed:</b> {{ accommodation.petsAllowed }}</div>
                            <div><b>Children Allowed:</b> {{ accommodation.childrenAllowed }}</div>
                            <div><b>General Amenities:</b> {{ accommodation.amenities ? accommodation.amenities.join(', ')
                              : '' }}</div>
                            <div><b>Recreation Amenities:</b> {{ accommodation.recreationAmenities ?
                              accommodation.recreationAmenities.join(', ') : '' }}</div>
                            <div><b>Water Amenities:</b> {{ accommodation.waterAmenities ?
                              accommodation.waterAmenities.join(', ') : '' }}</div>
                            <div><b>Credit Cards Accepted:</b> {{ accommodation.creditCards ?
                              accommodation.creditCards.join(', ') : '' }}</div>
                            <div><b>Discounts Offered:</b> {{ accommodation.discounts ? accommodation.discounts.join(', ')
                              : '' }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="attraction-info" v-if="location.attractions">
                      <div v-for="(attraction, index) in location.attractions" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> Attraction</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div><b>Admission Fee:</b> {{ attraction.fees }}</div>
                            <div><b>Fees:</b> {{ attraction.cost }}</div>
                          </div>
                          <div class="col-lg-6">
                            <div><b>Credit Cards Accepted:</b> {{ attraction.creditCards ?
                              attraction.creditCards.join(',') : '' }}</div>
                            <div><b>Discounts Offered:</b> {{ attraction.discounts ? attraction.discounts.join(', ') :
                              '' }}</div>
                            <div v-if="attraction.phone">
                              <b>Telephone:</b> {{ attraction.phone | telephoneNumber }}
                            </div>
                            <div><b>Hours of Operation:</b> {{ attraction.hoursOfOperation }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="food-info" v-if="location.food">
                      <div v-for="(food, index) in location.food" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> Food & Beverage Service</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div v-if="food.menu && food.menu.includes('http')">
                              <b>Menu Url: </b><a :href="food.menu" alt="Opens in a new window" rel="external noopener noreferrer"
                                target="_blank">{{ food.menu }}</a>
                            </div>
                            <div v-else><b>Menu Url: </b>{{ food.menu }}</div>
                          </div>
                          <div class="col-lg-6">
                            <div><b>Credit Cards Accepted:</b> {{ food.creditCards ? food.creditCards.join(', ') : '' }}
                            </div>
                            <div><b>Discounts Offered:</b> {{ food.discounts ? food.discounts.join(', ') : '' }}</div>
                            <div v-if="food.phone"><b>Telephone:</b> {{ food.phone | telephoneNumber }}</div>
                            <div><b>Hours of Operation:</b> {{ food.hoursOfOperation }}</div>
                          </div>
                        </div>

                        <div class="row">
                          <div class="col-lg-12">
                            <div><b>Featured in the BBQ Trail:</b>{{ food.featured }}</div>
                            <div>
                              <b>BBQ Trail Images</b>
                              <!-- <div class="col-lg-4" v-for="photo in food.photos" v-bind:key="photo">
                          <b-card :key="photo._id"
                          :title="photo.image_name"
                          :img-src="`https://alabama-travel.s3.amazonaws.com/partners-uploads/photo/image/${photo._id}/${photo.image}`"
                          img-alt=""
                          img-top
                          tag="article"
                          class="mb-4"
                          >
                          <b-card-text>
                          <b>Featured:</b> {{photo.featured}} <br/>
                          <b>Description:</b> {{ photo.description }}
                          </b-card-text>
                          </b-card>
                          </div> -->
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="retail-info">
                      <div v-for="(retail, index) in location.retails" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> Retail</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div v-if="retail.phone"><b>Telephone:</b> {{ retail.phone | telephoneNumber }}</div>
                          </div>
                          <div class="col-lg-6">
                            <div><b>Credit Cards Accepted:</b> {{ retail.creditCards ? retail.creditCards.join(', ') : ''
                            }}</div>
                            <div><b>Discounts Offered:</b> {{ retail.discounts ? retail.discounts.join(', ') : '' }}</div>
                            <div><b>Hours of Operation:</b> {{ retail.hoursOfOperation }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="service-info">
                      <div v-for="(service, index) in location.services" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> Service</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div v-if="service.phone">
                              <b>Telephone:</b> {{ service.phone | telephoneNumber }}
                            </div>
                          </div>
                          <div class="col-lg-6">
                            <div><b>Credit Cards Accepted:</b> {{ service.creditCards ? service.creditCards.join(', ') :
                              '' }}</div>
                            <div><b>Discounts Offered:</b> {{ service.discounts ? service.discounts.join(', ') : '' }}
                            </div>
                            <div><b>Hours of Operation:</b> {{ service.hoursOfOperation }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="shoppingDestination-info">
                      <div v-for="(shoppingDestination, index) in location.shoppingDestinations" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> Shopping Destination</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div v-if="shoppingDestination.phone">
                              <b>Telephone:</b> {{ shoppingDestination.phone | telephoneNumber }}
                            </div>
                          </div>
                          <div class="col-lg-6">
                            <div><b>Credit Cards Accepted:</b> {{ shoppingDestination.creditCards ?
                              shoppingDestination.creditCards.join(', ') : '' }}</div>
                            <div><b>Discounts Offered:</b> {{ shoppingDestination.discounts ?
                              shoppingDestination.discounts.join(', ') : '' }}</div>
                            <div><b>Hours of Operation:</b> {{ shoppingDestination.hoursOfOperation }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="venue-info" v-if="location.venues">
                      <div v-for="(venue, index) in location.venues" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> Venue</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div v-if="venue.scheduleUrl && venue.scheduleUrl.includes('http')">
                              <b>Schedule URL: </b><a :href="venue.scheduleUrl" alt="Opens in a new window"
                                rel="external noopener noreferrer" target="_blank">{{ venue.scheduleUrl }}</a>
                            </div>
                            <div v-else><b>Schedule URL: </b>{{ venue.scheduleUrl }}</div>
                            <div v-if="venue.ticketUrl && venue.ticketUrl.includes('http')">
                              <b>Tickets URL: </b><a :href="venue.ticketUrl" alt="Opens in a new window"
                                rel="external noopener noreferrer" target="_blank">{{ venue.ticketUrl }}</a>
                            </div>
                            <div v-else><b>Tickets URL: </b>{{ venue.scheduleUrl }}</div>
                          </div>
                          <div class="col-lg-6">
                            <div><b>Credit Cards Accepted:</b> {{ venue.creditCards ? venue.creditCards.join(', ') : '' }}
                            </div>
                            <div><b>Discounts Offered:</b> {{ venue.discounts ? venue.discounts.join(', ') : '' }}</div>
                            <div v-if="venue.telephone">
                              <b>Telephone:</b> {{ venue.telephone | telephoneNumber }}
                            </div>
                            <div><b>Hours of Operation:</b> {{ venue.hoursOfOperation }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- end venue -->
                    <div class="informationSource-info">
                      <div v-for="(informationSource, index) in location.informationSources" :key="index">
                        <div class="row mt-3">
                          <div class="col-lg-6">
                            <div><b>Category:</b> Information Source</div>
                            <div><b>Location Type:</b> {{ location._type }}</div>
                            <div v-if="informationSource.phone">
                              <b>Telephone:</b> {{ informationSource.phone | telephoneNumber }}
                            </div>
                            <div><b>Hours of Operation:</b> {{ informationSource.hoursOfOperation }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-card>
                </div>

                <div class="images">
                  <h2 class="mt-4">Images</h2>
                  <div class="row">
                    <div class="col-lg-4" v-for="photo in location.photos" v-bind:key="photo">
                      <b-card :key="photo._id" :title="photo.image_name"
                        :img-src="`https://alabama-travel.s3.amazonaws.com/partners-uploads/photo/image/${photo._id}/${photo.image}`"
                        img-alt="" img-top tag="article" class="mb-4">
                        <b-card-text>
                          <b>Featured:</b> {{ photo.featured }} <br />
                          <b>Description:</b> {{ photo.description }}
                        </b-card-text>
                      </b-card>
                    </div>
                  </div>
                </div>
                <div class="videos" v-if="location.video">
                  <h2 class="mt-4">Videos</h2>
                  <div><b>Youtube URL:</b>{{ location.video }}</div>
                  <div><b>Video Description/Credit:</b> {{ location.video_credit }}</div>
                  <div>
                    <b-embed type="iframe" aspect="16by9" :src="youtube_parser" allowfullscreen></b-embed>
                  </div>
                </div>
                <div v-if="location.activities && location.activities.length > 0" class="activities-section">
                  <h2 class="mt-4">Activities</h2>
                  <div class="row">
                    <div class="" v-for="(activity, index) in location.activities" :key="index">
                      <div class="activity-container card">
                        <div><b>Name:</b> {{ activity.name }}</div>
                        <div><b>Frequency:</b> {{ activity.frequency }}</div>
                        <div><b>Rate:</b> {{ activity.rate }}</div>
                        <div>
                          <b>Description: </b> <span v-html="activity.description">{{ activity.description }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab>
              <b-tab title="Preview">
                <iframe :src="previewUrl" class="preview-frame" />
              </b-tab>
              <b-tab title="History">
                <div class="container">
                  <div class="row">
                    <div class="col-12 text-left mt-4" v-if="location.updated_by">
                      <span class="">Current Version is {{ location.version }} by {{ location.updated_by.first_name }} {{
                        location.updated_by.last_name }}</span>
                    </div>
                    <div class="col-12 text-left" v-if="location.versions">
                      <table class="table table-bordered" v-if="location.reversedVersions">
                        <template v-for="(version, versionKey) in location.reversedVersions">
                          <thead :key="versionKey">
                            <tr>
                              <th colspan="3" class="text-center">
                                Version: {{ version.version }} changed on {{ version.updated_at | dateAtTime }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td class="td-parent" colspan="1"><b>Field</b></td>
                              <td class="td-parent" colspan="1">
                                <b>Old</b>
                                <span v-if="versionKey < location.reversedVersions.length - 1">
                                  ({{ location.reversedVersions[versionKey + 1]['updated_by'] }})</span>
                              </td>
                              <td class="td-parent" colspan="1"><b>New</b> ({{ version.updated_by }})</td>
                            </tr>
                            <tr class="mb-1" v-for="(key, index) in Object.keys(version)" :key="index">
                              <template v-if="versionKey < location.reversedVersions.length - 1 &&
                                hasValueChanged(key, version[key], location.reversedVersions[versionKey + 1][key])
                                ">
                                <td class="td-child" colspan="1">{{ key }}</td>
                                <td v-if="typeof location.reversedVersions[versionKey + 1][key] !== 'object'"
                                  class="td-child" colspan="1">
                                  {{ location.reversedVersions[versionKey + 1][key] }}
                                </td>
                                <td v-else class="td-child" colspan="1">
                                  <!-- Old Value-->
                                  <ul>
                                    <li v-for="(val, index) in location.reversedVersions[versionKey + 1][key]"
                                      :key="index">
                                      <span v-if="key === 'experience_types'">{{ val.name.toString() }}</span>
                                      <span v-else>{{ val }}</span>
                                    </li>
                                  </ul>
                                </td>
                                <td v-if="typeof version[key] !== 'object'" class="td-child" colspan="1">{{ version[key]
                                }}</td>
                                <td v-else class="td-child" colspan="1">
                                  <!-- New Value-->
                                  <ul>
                                    <li v-for="(val, index) in version[key]" :key="index">
                                      <span v-if="key === 'experience_types'">{{ val.name.toString() }}</span>
                                      <span v-else>{{ val }}</span>
                                    </li>
                                  </ul>
                                </td>
                              </template>
                              <template v-if="versionKey && versionKey == location.reversedVersions.length - 1">
                                <td class="td-child" colspan="1">{{ key }}</td>
                                <td></td>
                                <td v-if="typeof version[key] !== 'object'" class="td-child" colspan="1">{{ version[key]
                                }}</td>
                                <td v-else class="td-child" colspan="1">
                                  <ul>
                                    <li v-for="(val, index) in version[key]" :key="index">
                                      {{ val }}
                                    </li>
                                  </ul>
                                </td>
                              </template>
                            </tr>
                          </tbody>
                        </template>
                      </table>
                    </div>
                  </div>
                </div>
              </b-tab>

              <b-tab title="Pending" v-if="location.pendingChangeRequest">
                <div class="actions text-right mt-2 mb-2"
                  v-if="$store.state.auth.user.role === 'admin' || $store.state.auth.user.role === 'editor'">
                  <b-button class="mr-1"
                    v-on:click="approveChangeRequest(location.changeRequest._id, location._id)">Approve</b-button>
                  <b-button class="mr-3" v-on:click="rejectChangeRequest(location.changeRequest._id)">Reject</b-button>
                </div>
                <div class="container">
                  <div class="row">
                    <div class="col-12 text-left">
                      <table class="table table-bordered">
                        <template>
                          <thead>
                            <tr v-if="location.reversedVersions">
                              <th colspan="3" class="text-center">
                                Version: {{ location.changeRequest.version }} changed on
                                {{ location.changeRequest.updated_at | dateAtTime }}
                              </th>
                            </tr>
                          </thead>
                          <tbody v-if="location.reversedVersions">
                            <tr>
                              <td class="td-parent" colspan="1"><b>Field</b></td>
                              <td class="td-parent" colspan="1">
                                <b>Current</b>
                                <span v-if="versionKey < location.reversedVersions.length - 1">
                                  ({{ location.reversedVersions[versionKey + 1]['updated_by'] }})</span>
                              </td>
                              <td class="td-parent" colspan="1"><b>New</b></td>
                            </tr>
                            <tr class="mb-1" v-for="(key, index) in Object.keys(location)" :key="index">
                              <template v-if="hasValueChanged(key, location.changeRequest.listing[key], location[key])">
                                <td class="td-child" colspan="1">{{ key }}</td>
                                <!-- CURRENT or oldValues -->
                                <td v-if="key === 'experience_types'" class="td-child" colspan="1">{{
                                  location[key].map((x) => x.name).toString() }}</td>
                                <td v-else-if="key === 'photos'" class="td-child" colspan="1">
                                  <b>Number of Photos : {{ location[key].length }}</b><br />{{ location[key] }}
                                </td>
                                <td v-else class="td-child" colspan="1">{{ location[key] }}</td>
                                <!-- NEW or newValues -->
                                <!-- confirm, not sure if locations have start_on end_on as well -->
                                <td v-if="key === 'start_on'" class="td-child" colspan="1">
                                  {{ location.changeRequest.listing[key] | dateOnly }}
                                </td>
                                <td v-else-if="key === 'end_on'" class="td-child" colspan="1">
                                  {{ location.changeRequest.listing[key] | dateOnly }}
                                </td>
                                <td v-else-if="key === 'experience_types'" class="td-child" colspan="1">
                                  {{ location.changeRequest.listing[key].map((x) => x.name).toString() }}
                                </td>
                                <td v-else-if="key === 'photos'" class="td-child" colspan="1">
                                  <b>Number of Photos : {{ location.changeRequest.listing[key].length }}</b><br />{{
                                    location.changeRequest.listing[key] }}
                                </td>
                                <td v-else class="td-child" colspan="1">{{ location.changeRequest.listing[key] }}</td>
                              </template>
                              <!-- <template v-if="versionKey == location.reversedVersions.length -1">
                            <td class="td-child" colspan="1">{{key}}</td>
                            <td></td>
                             <td v-if="typeof version[key] !== 'object'" class="td-child" colspan="1">{{version[key]}}</td>
                              <td v-else class="td-child" colspan="1">
                                <ul>
                                  <li v-for="(val, index) in version[key]" :key="index">
                                   {{val}}
                                  </li>
                                </ul>
                              </td>
                          </template> -->
                            </tr>
                          </tbody>
                          <!-- This is a brand new location -->
                          <tbody v-else>
                            <tr>
                              <td class="td-parent" colspan="1"><b>Field</b></td>
                              <td class="td-parent" colspan="1"><b>Value</b></td>
                            </tr>
                            <tr class="mb-1" v-for="(key, index) in Object.keys(location)" :key="index">
                              <td class="td-child" colspan="1">{{ key }}</td>
                              <td class="td-child" colspan="1">{{ location[key] }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </table>
                    </div>
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>
    <b-modal id="org-modal" title="What Organization Are You Claiming This For?">
      <b-form-select id="org" name="org" v-model="chooserOrg">
        <option :value="null">Select Organization</option>
        <option v-for="(org, index) in userOrgs" :value="org.value" :key="index">{{ org.text }}</option>
      </b-form-select>

      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="danger" @click="cancel()"> Cancel </b-button>
        <b-button size="sm" variant="success" @click="claimLocationForOrg()"> Claim </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import deepEqual from 'deep-equal';

export default {
  components: {},
  data() {
    return {
      location: {
        accommodations: [],
      },
      coords: { lat: 0, lng: 0 },
      map: {
        zoom: 6,
        center: {
          lat: 33,
          lng: -86.8104,
        },
      },
      accommodationTypes: ['BedAndBreakfast', 'Campground', 'HotelOrMotel', 'SportsmanLodgeOrCabin', 'VacationRental'],
      attractionTypes: [
        'AmusementPark',
        'BotanicalGardens',
        'GolfCourse',
        'HistoricSite',
        'IndoorRecreationSite',
        'IndustryManufacturing',
        'Museum',
        'NationalPark',
        'OtherPark',
        'OutdoorRecreationSite',
        'ScienceCenter',
        'SportsFacility',
        'StatePark',
        'ZoologicalPark',
      ],
      foodTypes: [
        'Restaurant',
        'Bakery',
        'BarLoungeNightclubOrTavern',
        'BreweryOrDistillery',
        'CoffeeShopOrTeaRoom',
        'DelicatessenLunchCounterOrSodaFountain',
        'IceCreamOrDessertShop',
        'FoodTruck',
      ],
      retailTypes: ['AntiquesAndCollectibles', 'ArtGallery', 'Bookstore', 'Boutique', 'HomeAndGarden', 'Outdoor', 'Secondhand', 'SpecialtyFoodAndBeverage'],
      serviceTypes: ['BoatCharter', 'GuidesToursOrRides', 'OutfitterOrEquipmentRental', 'Spa'],
      shoppingDestinationTypes: ['FarmersMarket', 'FleaMarket', 'OutletMall', 'ShoppingCenter', 'ShoppingDistrict', 'ShoppingMall'],
      venueTypes: [
        'Amphitheater',
        'Arena',
        'Cinema',
        'ConcertHall',
        'ConferenceCenter',
        'ExhibitionHall',
        'FairGrounds',
        'MusicHall',
        'Racetrack',
        'Stadium',
        'Theater',
      ],
      informationSourceTypes: ['DMO', 'Organization', 'VisitorCenter'],
      userOrgs: [],
      chooserOrg: null,
    };
  },
  props: {
    locationId: {
      type: String,
      required: true,
    },
  },
  computed: {
    previewUrl() {
      return `${process.env.VUE_APP_WEB_ROOT}places-to-go/${this.location.slug}`;
    },
    youtube_parser() {
      const videoFullUrl = this.location.video;
      const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/; // eslint-disable-line
      const match = videoFullUrl.match(regExp);
      const value = match && match[7].length === 11 ? match[7] : false;
      const embedValue = `https://www.youtube.com/embed/${value}?rel=0`;
      return embedValue;
    },
  },
  async created() {
    await this.loadLocationDetails();
  },
  methods: {
    async loadLocationDetails() {
      const response = await axios.get(`${process.env.VUE_APP_API_LOCATIONS_ROOT}/${this.locationId}`);
      if (response.data.pendingChangeRequest === true) {
        this.location = response.data;
        this.location.photos = response.data.changeRequest.photos;
        this.location.experiences = response.data.experiences;
      } else {
        this.location = response.data.location;
      }
      if (this.location && this.location.versions) {
        this.location.reversedVersions = this.location.versions.slice().reverse();
      }
      if (this.location.latitude) {
        this.coords.lat = parseFloat(this.location.latitude);
        this.coords.lng = parseFloat(this.location.longitude);
        this.map.zoom = 13;
        this.map.center = this.coords;
      }
      this.location.accommodations = [];
      this.location.attractions = [];
      this.location.food = [];
      this.location.retails = [];
      this.location.shoppingDestinations = [];
      this.location.venues = [];
      this.location.services = [];
      this.location.informationSources = [];
      if (this.location._type) {
        this.location.category = this.setLocationCategory(this.location._type);
      }
      this.setLocationDetails(this.location._type);

      /*  Locations has a bug where if if exp types non-existent or [], it will render the page blank. Setting an hoc experience type of 'Not Set' so that
          downstream elements won't error when trying to call toString() -CSD */
      if (!this.location.experience_types || this.location.experience_types.length === 0) {
        this.location.experience_types = [];
        const subValues = { value: 11, name: 'Not Set' };
        this.location.experience_types.push(subValues);
      }

      // process current location's experience_types if unformatted.
      if (!Object.prototype.hasOwnProperty.call(this.location.experience_types[0], 'value')) {
        this.location.experience_types_unformatted = this.location.experience_types;
        this.location.experience_types_formatted = [];
        this.location.experience_types = this.getExperienceTypes(this.location.experience_types_formatted, this.location.experience_types_unformatted);
      }

      /* reversedVersions is a large object with multiple location versions. Map each of the version's experience types, if it doesnt have property name 'value', then
         call function to modify the version's experience_types to name/value format. --CSD
      */
      /* i am adding an if statement for reversed versions due to blank detail page errors --JP */
      if (this.location.reversedVersions) {
        this.location.reversedVersions.map((x) => (!Object.prototype.hasOwnProperty.call(x.experience_types, 'value') ? this.formatExperienceTypes(x.experience_types) : x.experience_types));
      }

      // process location's change request experience_types if unformatted.
      if (this.location.changeRequest && this.location.changeRequest.listing !== null) {
        if (!Object.prototype.hasOwnProperty.call(this.location.changeRequest.listing.experience_types[0], 'value')) {
          this.location.changeRequest.listing.experience_types_unformatted = this.location.changeRequest.listing.experience_types;
          this.location.changeRequest.listing.experience_types_formatted = [];
          this.location.changeRequest.listing.experience_types = this.getExperienceTypes(
            this.location.changeRequest.listing.experience_types_formatted,
            this.location.changeRequest.listing.experience_types_unformatted
          );
        }
        // Create an ad hoc sibling property in CR listing -- 'photos' -- so that hasValueChanged function can properly match on key and evaluate.
        this.location.changeRequest.listing.photos = this.location.changeRequest.photos;
      }
    },
    userIsInOrg() {
      if (this.location.organization) {
        return this.location.organization.user_ids.find((u) => u === this.$store.state.auth.mUser._id);
      }
      return false;
    },
    hasValueChanged(key, newVal, oldVal) {
      if (
        key === 'version'
        || key === 'updated_at'
        || key === 'updated_by'
        || key === 'reversedVersions'
        || key === 'changeRequest'
        || key === 'changeRequestId'
        || key === 'pendingChangeRequest'
        || key === 'versions'
        || key === 'organization'
        || key === 'changeRequestType'
        || key === 'experiences'
        || key === 'experiences_ids'
        || key === 'experience_types_unformatted'
        || key === 'experience_types_formatted'
        || key === '__v'
      ) {
        return false;
      }
      if ((typeof oldVal !== 'object' || typeof newVal !== 'object') && newVal !== oldVal && newVal !== undefined) {
        if (key === 'start_on' || key === 'end_on') {
          // normalize all incoming datetimes to mm/dd/yyyy
          const newValDate = new Date(newVal).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
          const oldValDate = new Date(oldVal).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
          // if newValdate and oldValdate are the same, return false, "no change"
          if (newValDate === oldValDate) {
            return false;
          }
        }
        return true;
      }
      if (typeof oldVal === 'object' || typeof newVal === 'object') {
        const isEqual = deepEqual(newVal, oldVal);
        // if  obj equality is false, oldVal and newVal are different and should be displayed
        if (oldVal && newVal && isEqual === false) {
          return !oldVal.every((e) => newVal.includes(e));
        }
      }
      return false;
    },
    setLocationCategory(type) {
      if (this.attractionTypes.find((name) => name === type)) {
        return 'attraction';
      }
      if (this.accommodationTypes.find((name) => name === type)) {
        return 'accommodation';
      }
      if (this.foodTypes.find((name) => name === type)) {
        return 'food';
      }
      if (this.retailTypes.find((name) => name === type)) {
        return 'retail';
      }
      if (this.serviceTypes.find((name) => name === type)) {
        return 'service';
      }
      if (this.shoppingDestinationTypes.find((name) => name === type)) {
        return 'shoppingDestination';
      }
      if (this.venueTypes.find((name) => name === type)) {
        return 'venue';
      }
      if (this.informationSourceTypes.find((name) => name === type)) {
        return 'informationSource';
      }
      return '';
    },
    setLocationDetails(type) {
      this.location._type = type;
      switch (this.location.category) {
        case 'accommodation':
          this.location.accommodations.push(this.populateaccommodation(type));
          break;
        case 'attraction':
          this.location.attractions.push(this.populateAttraction(type));
          break;
        case 'food':
          this.location.food.push(this.populateFood(type));
          break;
        case 'retail':
          this.location.retails.push(this.populateRetail(type));
          break;
        case 'service':
          this.location.services.push(this.populateService(type));
          break;
        case 'shoppingDestination':
          this.location.shoppingDestinations.push(this.populateShoppingDestination(type));
          break;
        case 'venue':
          this.location.venues.push(this.populateVenue(type));
          break;
        case 'informationSource':
          this.location.informationSources.push(this.populateInformationSource(type));
          break;
        default:
          break;
      }
    },
    populateaccommodation(type) {
      return {
        _type: type,
        rates: this.location.rates,
        reservationsUrl: this.location.reservations_url,
        petsAllowed: this.location.pets_allowed,
        childrenAllowed: this.location.children_allowed,
        amenities: this.location.general_amenities ? this.location.general_amenities : [],
        creditCards: this.location.credit_cards ? this.location.credit_cards : [],
        waterAmenities: this.location.water_amenities ? this.location.water_amenities : [],
        discounts: this.location.discounts,
        phone: this.location.reservations_phone,
        meetingFacilities: this.location.facilities,
        meetingCapacity: this.location.facilities_capacity,
        capacity: this.location.capacity,
        hoursOfOperation: this.location.hours_of_operation,
        recreationAmenities: this.location.recreation_amenities ? this.location.recreation_amenities : [],
      };
    },
    populateAttraction(type) {
      return {
        _type: type,
        phone: this.location.telephone,
        cost: this.location.cost,
        fees: this.location.fees,
        discounts: this.location.discounts ? this.location.discounts : [],
        creditCards: this.location.credit_cards ? this.location.credit_cards : [],
        hoursOfOperation: this.location.hours_of_operation,
      };
    },
    populateFood(type) {
      return {
        _type: type,
        menu: this.location.menu_url,
        phone: this.location.telephone,
        discounts: this.location.discounts ? this.location.discounts : [],
        hoursOfOperation: this.location.hours_of_operation,
        creditCards: this.location.credit_cards ? this.location.credit_cards : [],
      };
    },
    populateRetail(type) {
      return {
        _type: type,
        phone: this.location.telephone,
        discounts: this.location.discounts ? this.location.discounts : [],
        hoursOfOperation: this.location.hours_of_operation,
        creditCards: this.location.credit_cards ? this.location.credit_cards : [],
      };
    },
    populateShoppingDestination(type) {
      return {
        _type: type,
        phone: this.location.telephone,
        discounts: this.location.discounts ? this.location.discounts : [],
        hoursOfOperation: this.location.hours_of_operation,
        creditCards: this.location.credit_cards ? this.location.credit_cards : [],
      };
    },
    populateVenue(type) {
      return {
        _type: type,
        telephone: this.location.telephone,
        discounts: this.location.discounts ? this.location.discounts : [],
        scheduleUrl: this.location.schedule_url,
        ticketUrl: this.location.tickets_url,
        creditCards: this.location.credit_cards ? this.location.credit_cards : [],
        hoursOfOperation: this.location.hours_of_operation,
      };
    },
    populateService(type) {
      return {
        _type: type,
        phone: this.location.telephone,
        discounts: this.location.discounts ? this.location.discounts : [],
        hoursOfOperation: this.location.hours_of_operation,
        creditCards: this.location.credit_cards ? this.location.credit_cards : [],
      };
    },
    populateInformationSource(type) {
      return {
        _type: type,
        phone: this.location.telephone,
        hoursOfOperation: this.location.hours_of_operation,
      };
    },
    getExperienceTypes(obj, experienceTypes) {
      if (experienceTypes) {
        experienceTypes.forEach((type) => {
          switch (type) {
            case 1:
              obj.push({ value: 1, name: 'History' });
              break;
            case 2:
              obj.push({ value: 2, name: 'Beaches' });
              break;
            case 3:
              obj.push({ value: 3, name: 'Food' });
              break;
            case 4:
              obj.push({ value: 4, name: 'Arts & Culture' });
              break;
            case 5:
              obj.push({ value: 5, name: 'Civil Rights' });
              break;
            case 6:
              obj.push({ value: 6, name: 'Music' });
              break;
            case 7:
              obj.push({ value: 7, name: 'Golf' });
              break;
            case 8:
              obj.push({ value: 8, name: 'Outdoors' });
              break;
            case 9:
              obj.push({ value: 9, name: 'Science & Discovery' });
              break;
            case 10:
              obj.push({ value: 10, name: 'Sports' });
              break;
            case 0:
              obj.push({ value: 11, name: 'Not Set' });
              break;
            default:
              break;
          }
        });
      }
      return obj;
    },
    formatExperienceTypes(experienceTypes) {
      if (experienceTypes) {
        experienceTypes.forEach((type, index) => {
          switch (type) {
            case 1:
              experienceTypes[index] = { value: 1, name: 'History' };
              break;
            case 2:
              experienceTypes[index] = { value: 2, name: 'Beaches' };
              break;
            case 3:
              experienceTypes[index] = { value: 3, name: 'Food' };
              break;
            case 4:
              experienceTypes[index] = { value: 4, name: 'Arts & Culture' };
              break;
            case 5:
              experienceTypes[index] = { value: 5, name: 'Civil Rights' };
              break;
            case 6:
              experienceTypes[index] = { value: 6, name: 'Music' };
              break;
            case 7:
              experienceTypes[index] = { value: 7, name: 'Golf' };
              break;
            case 8:
              experienceTypes[index] = { value: 8, name: 'Outdoors' };
              break;
            case 9:
              experienceTypes[index] = { value: 9, name: 'Science & Discovery' };
              break;
            case 10:
              experienceTypes[index] = { value: 10, name: 'Sports' };
              break;
            case 0:
              experienceTypes[index] = { value: 11, name: 'Not Set' };
              break;
            default:
              break;
          }
        });
      }
      return experienceTypes;
    },
    async cancelChangeRequest(changeRequestId) {
      const response = await axios.get(`${process.env.VUE_APP_API_ROOT}/admin/cancelChangeRequest/${changeRequestId}`);
      if (response.data.success) {
        this.location.pendingChangeRequest = false;
        this.location.changeRequestId = null;
        this.$bvToast.toast(`${response.data.message}`, {
          title: 'Success',
          autoHideDelay: 5000,
          appendToast: true,
        });
      } else {
        this.$bvToast.toast(`${response.data.message}`, {
          title: 'Error',
          autoHideDelay: 5000,
          appendToast: true,
        });
      }
    },
    async approveChangeRequest(requestId, locationId) {
      const requestInfo = {
        change_request_id: requestId,
        location_id: locationId,
      };
      await axios.post(`${process.env.VUE_APP_API_LOCATIONS_ROOT}/locations/approveLocationChangeRequest`, JSON.stringify(requestInfo)).then(
        async (response) => {
          const changeResponse = await axios.get(`${process.env.VUE_APP_API_ROOT}/admin/approveChangeRequest/${requestId}`);
          if (changeResponse.data.success) {
            const self = this;
            if (!requestInfo.location_id) {
              // location ADD type
              this.$router.push({ name: 'locations detail', params: { locationId: response.data.locationId } }, () => {
                setTimeout(async () => {
                  self.$bvToast.toast(`${JSON.stringify(response.data.message)}`, { title: 'Success', autoHideDelay: 5000, appendToast: true });
                  await this.$store.dispatch('changeRequests/getPendingLocations');
                  await this.loadLocationDetails();
                  this.$refs.detailsTab.activate();
                }, 500);
              });
            } else {
              // location EDIT type
              setTimeout(async () => {
                self.$bvToast.toast(`${response.data.message}`, { title: 'Success', autoHideDelay: 5000, appendToast: true });
                await this.$store.dispatch('changeRequests/getPendingLocations');
                await this.loadLocationDetails();
                this.$refs.detailsTab.activate();
              }, 500);
            }
          } else {
            this.$bvToast.toast(`${changeResponse.data.message}`, {
              title: 'Error',
              autoHideDelay: 5000,
              appendToast: true,
            });
          }
        },
        (error) => {
          this.$bvToast.toast(`${error.response.data}`, {
            title: 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        }
      );
    },
    async claimLocation() {
      const response = await axios.get(`${process.env.VUE_APP_API_ORGANIZATIONS_ROOT}/getOrgsForUser/${this.$store.state.auth.mUser._id}`);
      const orgs = response.data.userOrganizations;
      this.userOrgs = orgs.map((o) => ({ text: o.name, value: o._id }));

      if (this.userOrgs.length === 0) {
        // eslint-disable-next-line no-alert
        alert('You must be a member of an organization to submit a claim request.');
      } else if (this.userOrgs.length > 1) {
        this.$bvModal.show('org-modal');
      } else {
        const claimResponse = await axios.post(`${process.env.VUE_APP_API_LOCATIONS_ROOT}/claimLocation`, {
          organizationId: this.userOrgs[0].value,
          locationId: this.locationId,
        });
        if (claimResponse.status === 200) {
          this.$bvToast.toast('Claim successfully submitted.');
        } else {
          this.$bvToast.toast('Something went wrong in submitting the claim.  Please try again later.');
        }
      }
    },
    async claimLocationForOrg() {
      this.$bvModal.hide('org-modal');
      const claimResponse = await axios.post(`${process.env.VUE_APP_API_LOCATIONS_ROOT}/claimLocation`, {
        organizationId: this.chooserOrg,
        locationId: this.locationId,
      });
      if (claimResponse.status === 200) {
        this.$bvToast.toast('Claim successfully submitted.');
      } else {
        this.$bvToast.toast('Something went wrong in submitting the claim.  Please try again later.');
      }
    },
  },
};
</script>

<style>
.activity-container {
  margin: 20px 20px;
  padding: 15px 15px;
  width: 500px;
}

.card-body h1 {
  margin: 0px 0 10px 0;
}

.container {
  margin-bottom: 150px;
}

.preview-frame {
  width: 100%;
  height: 100vh;
}
</style>
